import {gql} from '@apollo/client';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import type {
  PMEISidepanelDetailQuery,
  PMEISidepanelDetailQueryVariables,
} from './__generated/PMEISidepanelDetailQuery';

export const PMEI_SIDEPANEL_DETAIL_QUERY = gql`
  query PMEISidepanelDetailQuery($pmeiId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureMethodExecutionInstance(id: $pmeiId) {
      ... on ProcedureMethodExecutionInstanceNode {
        id
        worksheet {
          ... on WorksheetNode {
            id
            name
            displayId
            editUrl
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface PMEISidepanelDetailData {
  worksheetUrl: string | null;
  worksheetDisplayId: string | null;
}

export default function usePMEISidepanelDetail(pmeiId: string): {
  loading: boolean;
  data: PMEISidepanelDetailData | null;
} {
  const result = useBenchlingQuery<PMEISidepanelDetailQuery, PMEISidepanelDetailQueryVariables>(
    PMEI_SIDEPANEL_DETAIL_QUERY,
    {
      variables: {pmeiId},
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return {loading: result.type === QueryResultType.PENDING, data: null};
  }
  if (result.type === QueryResultType.ERROR) {
    return {loading: false, data: null};
  }

  const pmei = result.data.procedureMethodExecutionInstance;
  if (isInaccessible(pmei)) {
    return {loading: false, data: null};
  }

  const worksheet = pmei.worksheet;
  const hasWorksheet = !isInaccessible(worksheet);

  return {
    loading: false,
    data: {
      worksheetUrl: hasWorksheet ? worksheet.editUrl : null,
      worksheetDisplayId: hasWorksheet ? (worksheet.displayId ?? worksheet.name) : null,
    },
  };
}
